import React, { useState, useEffect } from "react";
import {
  MapPin,
  Clock,
  DollarSign,
  CheckCircle,
  ArrowLeft,
  ShoppingCart,
  Truck,
  AlertCircle,
  XCircle,
  Copy,
  Check,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/api";
import { Order, OrderStatusType } from "../types";
import { toast } from "react-hot-toast";
import ConfirmationModal from "./ConfirmationModal";
import RejectOrderModal from "./RejectOrderModal";

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    action: string;
    title: string;
    message: string;
  }>({ isOpen: false, action: "", title: "", message: "" });
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState(false);

  useEffect(() => {
    loadOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getOrderById(Number(orderId));
      console.log("[OrderDetail] Loaded order data:", data);
      console.log("[OrderDetail] Current status:", data.currentStatus);

      setOrder(data);
    } catch (error) {
      console.error("Failed to load order details:", error);
      toast.error("Failed to load order details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdateClick = (newStatus: string, label: string) => {
    const messages = {
      accepted: {
        title: "Accept Order",
        message: "Are you sure you want to accept this order? You'll be committing to buy these items.",
      },
      buying: {
        title: "Start Buying",
        message: "Are you ready to start shopping for the items at the market?",
      },
      waiting_delivery: {
        title: "Initiate Delivery",
        message: "Have you purchased all items and ready to hand them over for delivery?",
      },
    };

    const modalContent = messages[newStatus as keyof typeof messages] || {
      title: `Update to ${label}`,
      message: "Are you sure you want to update this order status?",
    };

    setConfirmModal({
      isOpen: true,
      action: newStatus,
      ...modalContent,
    });
  };

  const handleConfirmStatusUpdate = async () => {
    if (!order) return;

    try {
      setIsUpdatingStatus(true);
      await apiService.updateOrderStatus(order.id, {
        status: confirmModal.action as OrderStatusType,
      });
      toast.success(`Order status updated to ${formatStatus(confirmModal.action)}`);
      setConfirmModal({ isOpen: false, action: "", title: "", message: "" });
      await loadOrderDetails();
    } catch (error: any) {
      console.error("Failed to update order status:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleRejectOrder = async (reason: string) => {
    if (!order) return;

    try {
      setIsUpdatingStatus(true);
      await apiService.updateOrderStatus(order.id, {
        status: "cancelled" as OrderStatusType,
      });
      toast.success(`Order rejected: ${reason}`);
      setRejectModalOpen(false);
      await loadOrderDetails();
    } catch (error: any) {
      console.error("Failed to reject order:", error);
      toast.error(error.response?.data?.detail || "Failed to reject order");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleCopyOrderId = async () => {
    if (!order) return;
    try {
      await navigator.clipboard.writeText(order.orderId);
      setCopiedOrderId(true);
      toast.success("Order ID copied to clipboard");
      setTimeout(() => setCopiedOrderId(false), 2000);
    } catch (error) {
      toast.error("Failed to copy order ID");
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      submitted_for_buying: "bg-blue-100 text-blue-800",
      accepted: "bg-orange-100 text-orange-800",
      buying: "bg-purple-100 text-purple-800",
      waiting_delivery: "bg-yellow-100 text-yellow-800",
      submitted_for_delivery: "bg-indigo-100 text-indigo-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800";
  };

  const formatStatus = (status: string | undefined) => {
    if (!status) return "Unknown";
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getCurrentStatus = () => {
    if (!order || !order.currentStatus) return null;
    return order.currentStatus;
  };

  const getAvailableActions = () => {
    const currentStatus = getCurrentStatus();
    console.log("[OrderDetail] Getting available actions for status:", currentStatus);

    if (!currentStatus) {
      console.log("[OrderDetail] No current status found, returning empty actions");
      return [];
    }

    const actions: {
      status: string;
      label: string;
      description: string;
      icon: any;
      color: string;
      bgColor: string;
    }[] = [];

    console.log("[OrderDetail] Current status value:", currentStatus.status);

    switch (currentStatus.status) {
      case "submitted_for_buying":
        actions.push({
          status: "accepted",
          label: "Accept Order",
          description: "Accept this order and commit to buying the items",
          icon: CheckCircle,
          color: "bg-green-600 hover:bg-green-700",
          bgColor: "bg-green-50 border-green-200",
        });
        break;
      case "accepted":
        actions.push({
          status: "buying",
          label: "Start Buying",
          description: "Begin shopping for items at the market",
          icon: ShoppingCart,
          color: "bg-blue-600 hover:bg-blue-700",
          bgColor: "bg-blue-50 border-blue-200",
        });
        break;
      case "buying":
        actions.push({
          status: "waiting_delivery",
          label: "Initiate Delivery",
          description: "Items purchased, ready to hand over for delivery",
          icon: Truck,
          color: "bg-orange-600 hover:bg-orange-700",
          bgColor: "bg-orange-50 border-orange-200",
        });
        break;
      default:
        console.log("[OrderDetail] No action available for status:", currentStatus.status);
    }

    console.log("[OrderDetail] Available actions:", actions);
    return actions;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order || !order.marketList) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">
            {!order ? "Order not found" : "Order data incomplete"}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentStatus = getCurrentStatus();
  const availableActions = getAvailableActions();

  console.log("[OrderDetail] Rendering with currentStatus:", currentStatus);
  console.log("[OrderDetail] Rendering with availableActions:", availableActions);
  console.log("[OrderDetail] availableActions.length:", availableActions.length);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Order #{order.orderId}
                  </h1>
                  <button
                    onClick={handleCopyOrderId}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copy Order ID"
                  >
                    {copiedOrderId ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500">
                  {new Date(order.date).toLocaleDateString()} at{" "}
                  {new Date(order.date).toLocaleTimeString()}
                </p>
              </div>
            </div>
            {currentStatus && currentStatus.status && (
              <span
                className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(
                  currentStatus.status
                )}`}
              >
                {formatStatus(currentStatus.status)}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Order Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Market List Items */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-primary" />
                Market List: {order.marketList.title}
              </h2>
              <div className="space-y-3">
                {order.marketList.marketListItem?.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-semibold">
                          {index + 1}
                        </span>
                        <h3 className="font-medium text-gray-900">{item.name}</h3>
                      </div>
                      {item.comment && (
                        <p className="text-sm text-gray-500 ml-8 mt-1">
                          Note: {item.comment}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {item.cost.toFixed(0)} FCFA
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center font-semibold text-lg">
                  <span>Total Items Cost:</span>
                  <span className="text-primary">{order.cost.toFixed(0)} FCFA</span>
                </div>
              </div>
            </div>

            {/* Shopping Budget */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-primary" />
                Shopping Budget
              </h2>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800 mb-2">
                  Total amount to spend on items:
                </p>
                <p className="text-3xl font-bold text-orange-600">
                  {order.cost.toFixed(0)} FCFA
                </p>
                <p className="text-xs text-orange-600 mt-2">
                  Try to stay within this budget when buying items
                </p>
              </div>
            </div>

            {/* Current Status */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                Current Status
              </h2>
              {currentStatus && (
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full mt-1 bg-primary"></div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {formatStatus(currentStatus.status)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {new Date(currentStatus.date).toLocaleDateString()} at{" "}
                      {new Date(currentStatus.date).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Delivery & Actions */}
          <div className="space-y-6">
            {/* Delivery Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Delivery Information
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Market:</p>
                  <p className="font-medium text-gray-900">{order.market.name}</p>
                  <p className="text-sm text-gray-600">
                    {order.market.city.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Delivery Address:</p>
                  <p className="font-medium text-gray-900">
                    {order.deliveryLabel}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Coordinates:</p>
                  <p className="text-sm font-mono text-gray-700">
                    {order.deliveryLatitude.toFixed(6)},{" "}
                    {order.deliveryLongitude.toFixed(6)}
                  </p>
                  <a
                    href={`https://www.google.com/maps?q=${order.deliveryLatitude},${order.deliveryLongitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    Open in Maps →
                  </a>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {availableActions.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Next Action
                </h2>
                <div className="space-y-3">
                  {availableActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <div key={action.status} className={`border-2 ${action.bgColor} rounded-lg p-4`}>
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {action.description}
                        </p>
                        <button
                          onClick={() => handleStatusUpdateClick(action.status, action.label)}
                          disabled={isUpdatingStatus}
                          className={`w-full flex items-center justify-center gap-2 px-4 py-3 ${action.color} text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md`}
                        >
                          <Icon className="w-5 h-5" />
                          {action.label}
                        </button>
                      </div>
                    );
                  })}

                  {/* Reject Order Button - Only show for submitted_for_buying and accepted */}
                  {(currentStatus?.status === "submitted_for_buying" || currentStatus?.status === "accepted") && (
                    <div className="border-2 bg-red-50 border-red-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Unable to fulfill this order?
                      </p>
                      <button
                        onClick={() => setRejectModalOpen(true)}
                        disabled={isUpdatingStatus}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                      >
                        <XCircle className="w-5 h-5" />
                        Reject Order
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Order Status Messages */}
            {currentStatus?.status === "waiting_delivery" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <Truck className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                <p className="text-center text-blue-800 font-medium">
                  Waiting for Delivery Pickup
                </p>
                <p className="text-center text-sm text-blue-600 mt-1">
                  Your work is done! The delivery team will take it from here.
                </p>
              </div>
            )}

            {currentStatus?.status === "submitted_for_delivery" && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                <Truck className="w-12 h-12 text-indigo-600 mx-auto mb-2" />
                <p className="text-center text-indigo-800 font-medium">
                  Out for Delivery
                </p>
                <p className="text-center text-sm text-indigo-600 mt-1">
                  The delivery person is on their way to the customer.
                </p>
              </div>
            )}

            {currentStatus?.status === "delivered" && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <p className="text-center text-green-800 font-medium">
                  Order Completed
                </p>
                <p className="text-center text-sm text-green-600 mt-1">
                  This order has been successfully delivered to the customer.
                </p>
              </div>
            )}

            {currentStatus?.status === "cancelled" && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-2" />
                <p className="text-center text-red-800 font-medium">
                  Order Cancelled
                </p>
                <p className="text-center text-sm text-red-600 mt-1">
                  This order has been cancelled. No action is required.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: "", title: "", message: "" })}
        onConfirm={handleConfirmStatusUpdate}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Confirm"
        isLoading={isUpdatingStatus}
      />

      {/* Reject Order Modal */}
      <RejectOrderModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onReject={handleRejectOrder}
        orderId={order?.orderId || ""}
        isLoading={isUpdatingStatus}
      />
    </div>
  );
};

export default OrderDetail;
